import React, { useState, useEffect, useContext } from "react";
import {
  MdImage,
  MdThumbUp,
  MdThumbDown,
  MdChatBubbleOutline,
  MdFlag,
  MdBlock,
  MdSend,
  MdClose,
} from "react-icons/md"; // Import icons for actions
import { UserContext } from "../context/UserContext";
import axios from "axios";

function Comment({ selectedBill }) {
  const { emailId, firstName } = useContext(UserContext); // Access emailId and firstName from context
  const [newComment, setNewComment] = useState("");
  const [comments, setComments] = useState([]);
  const [wordCount, setWordCount] = useState(0);
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeReply, setActiveReply] = useState(null);
  const [newReply, setNewReply] = useState("");
  const [showReplyBox, setShowReplyBox] = useState(false);
  const [category, setCategory] = useState(""); // State to store the category
  const [currentPage, setCurrentPage] = useState(1);
  const [blockPopup, setBlockPopup] = useState({ visible: false, username: "", blockedEmail: "" });
  const [reportPopup, setReportPopup] = useState({ visible: false, username: "", reportedEmail: "", reason: "" });
  const [blockedEmailId, setBlockedEmailId] = useState(""); // <-- New state
  
  const COMMENTS_PER_PAGE = 10;

  // Fetch comments and category whenever selectedBill changes
  useEffect(() => {

      fetchComments();
  }, [emailId]);

  // Load replies for comments on current page
  useEffect(() => {
    const loadRepliesForCurrentPage = () => {
      const indexOfLastComment = currentPage * COMMENTS_PER_PAGE;
      const indexOfFirstComment = indexOfLastComment - COMMENTS_PER_PAGE;
      const currentComments = comments.slice(
        indexOfFirstComment,
        indexOfLastComment
      );

      // Fetch replies only for comments that haven't loaded them yet
      currentComments.forEach((comment) => {
        if (!comment.repliesLoaded) {
          fetchReplies(comment.Comment_Id);
        }
      });
    };

    loadRepliesForCurrentPage();
  }, [currentPage, comments]);

  // Fetch comments from the server
  const fetchComments = async () => {
    try {
      const response = await axios.get(
        `/api/general-comments`,
        {
          params: { emailId },
        }
      );

      if (response.status === 200 && Array.isArray(response.data)) {
        console.log("Fetched comments data:", response.data); // Debug log

        const data = response.data.map((comment) => ({
          Comment_Id: comment.Comment_Id,
          Username: comment.Username,
          emailId: comment.Email_Id || "",
          Datestamp: comment.Datestamp,
          Timestamp: comment.Timestamp,
          comments: comment.comments,
          media: comment.media || null,
          Likes: parseInt(comment.Likes) || 0,    // Parse Likes
          Dislikes: parseInt(comment.Dislikes) || 0, // Parse Dislikes
          userAction: comment.userAction || null,
          replies: [], // Initialize as empty
          repliesLoaded: false,
          category: comment.category_name,
          bill_title: comment.bill_title,
        }));

        console.log("Processed comments:", data); // Debug log
        setComments(data);
      } else {
        console.error("Unexpected response format:", response.data);
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
    }
  };

  const fetchReplies = async (commentId) => {
    try {
      const response = await axios.get(
        `/api/comments/${commentId}/replies`
      );

      if (response.status === 200 && Array.isArray(response.data)) {
        setComments((prev) =>
          prev.map((comment) =>
            comment.Comment_Id === commentId
              ? { ...comment, replies: response.data, repliesLoaded: true }
              : comment
          )
        );
      } else {
        console.error("Unexpected response format for replies:", response.data);
      }
    } catch (error) {
      console.error(`Error fetching replies for comment ${commentId}:`, error);
    }
  };

  // Fetch category from the server
  const fetchCategory = async () => {
    try {
      const response = await axios.get(
        `/api/bills/${encodeURIComponent(
          selectedBill
        )}/category`
      );
      if (response.status === 200 && response.data?.category) {
        setCategory(response.data.category);
      } else {
        setCategory(""); // Default to empty string if no category is returned
      }
    } catch (error) {
      console.error("Error fetching category:", error);
      setCategory(""); // Default to empty string on error
    }
  };

  const handleNewCommentChange = (e) => {
    const wordLimit = 400;
    const words = e.target.value.trim().split(/\s+/);

    if (words.length > wordLimit) {
      setNewComment(words.slice(0, wordLimit).join(" "));
      setWordCount(wordLimit);
    } else {
      setNewComment(e.target.value);
      setWordCount(words.length);
    }
  };

  const handleAddComment = async () => {
    if (!emailId) {
      alert("Please log in to add a comment");
      return;
    }

    if (newComment.trim() === "" && !selectedImage) {
      return;
    }

    const currentDate = new Date();
    const formData = new FormData();
    formData.append("emailId", emailId);
    formData.append("username", firstName);
    formData.append("comments", newComment);
    formData.append("billTitle", selectedBill);
    formData.append("date", currentDate.toISOString().split("T")[0]);
    formData.append("time", currentDate.toTimeString().split(" ")[0]);
    if (selectedImage) {
      formData.append("image", selectedImage);
    }

    try {
      const response = await axios.post(
        `/api/bills/${encodeURIComponent(
          selectedBill
        )}/comments`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 201) {
        const newCommentObj = {
          Comment_Id: Date.now(),
          Username: firstName,
          Datestamp: currentDate.toISOString().split("T")[0],
          Timestamp: currentDate.toTimeString().split(" ")[0],
          comments: newComment,
          Likes: 0,
          Dislikes: 0,
          userAction: null, // New comment has no user action yet
          media: selectedImage ? `/uploads/${response.data.fileName}` : null,
          replies: [],
        };

        setComments([newCommentObj, ...comments]);
        setNewComment("");
        setSelectedImage(null);
        setWordCount(0);
      }
    } catch (error) {
      console.error("Error saving comment:", error);
    }
  };

  const handleCancel = () => {
    setNewComment("");
    setSelectedImage(null);
    setWordCount(0);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedFileTypes = [
        "image/png",
        "image/jpg",
        "image/jpeg",
        "image/gif",
      ];
      const maxSize = 2 * 1024 * 1024;

      if (allowedFileTypes.includes(file.type) && file.size <= maxSize) {
        if (selectedImage) {
          alert("Per post there is allowance of 1 image/video");
        } else {
          setSelectedImage(file);
        }
      } else if (file.size > maxSize) {
        alert("Maximum size limit for uploading an image is 2MB.");
      } else {
        alert("Please select a valid image file (png, jpg, jpeg, gif).");
      }
    }
  };

  const handleLike = async (commentId) => {
    if (!emailId) {
      alert("Please log in to like comments");
      return;
    }

    // First update local state for immediate feedback
    setComments((prev) =>
      prev.map((comment) => {
        if (comment.Comment_Id === commentId) {
          let newLikes = comment.Likes;
          let newDislikes = comment.Dislikes;
          let newAction = comment.userAction;

          if (comment.userAction === "like") {
            // Undo like
            newLikes = Math.max(0, newLikes - 1);
            newAction = null;
          } else if (comment.userAction === "dislike") {
            // Switch from dislike to like
            newLikes = newLikes + 1;
            newDislikes = Math.max(0, newDislikes - 1);
            newAction = "like";
          } else {
            // New like
            newLikes = newLikes + 1;
            newAction = "like";
          }

          return {
            ...comment,
            Likes: newLikes,
            Dislikes: newDislikes,
            userAction: newAction
          };
        }
        return comment;
      })
    );

    // Then update the database
    try {
      const response = await axios.post("/api/comments/reaction", {
        emailId: emailId,
        reactionType: "like",
        targetId: commentId,
        targetType: "comment"
      });

      if (response.status !== 200) {
        // If server update fails, revert the local state
        fetchComments(); // Refresh comments to get correct state
      }
    } catch (error) {
      console.error("Error updating like:", error);
      alert("Failed to update like. Please try again.");
      fetchComments(); // Refresh comments on error
    }
  };

  const handleDislike = async (commentId) => {
    if (!emailId) {
      alert("Please log in to dislike comments");
      return;
    }

    // First update local state for immediate feedback
    setComments((prev) =>
      prev.map((comment) => {
        if (comment.Comment_Id === commentId) {
          let newLikes = comment.Likes;
          let newDislikes = comment.Dislikes;
          let newAction = comment.userAction;

          if (comment.userAction === "dislike") {
            // Undo dislike
            newDislikes = Math.max(0, newDislikes - 1);
            newAction = null;
          } else if (comment.userAction === "like") {
            // Switch from like to dislike
            newLikes = Math.max(0, newLikes - 1);
            newDislikes = newDislikes + 1;
            newAction = "dislike";
          } else {
            // New dislike
            newDislikes = newDislikes + 1;
            newAction = "dislike";
          }

          return {
            ...comment,
            Likes: newLikes,
            Dislikes: newDislikes,
            userAction: newAction
          };
        }
        return comment;
      })
    );

    // Then update the database
    try {
      const response = await axios.post("/api/comments/reaction", {
        emailId: emailId,
        reactionType: "dislike",
        targetId: commentId,
        targetType: "comment"
      });

      if (response.status !== 200) {
        // If server update fails, revert the local state
        fetchComments(); // Refresh comments to get correct state
      }
    } catch (error) {
      console.error("Error updating dislike:", error);
      alert("Failed to update dislike. Please try again.");
      fetchComments(); // Refresh comments on error
    }
  };

  const handleBlockUser = async () => {
    // Debugging: Log email values to check if they are missing
    console.log("Blocker Email:", emailId);
    console.log("Blocked Email:", blockedEmailId);
  
    // Ensure both emails are present
    if (!emailId || !blockedEmailId) {
      alert("Error: Missing user information.");
      return;
    }
  
    try {
      const response = await axios.post("/api/block-user", {
        blockerEmail: emailId, // ✅ Ensure Blocker Email is sent
        blockedEmail: blockedEmailId, // ✅ Ensure Blocked Email is sent
      });
  
      if (response.status === 200) {
        alert(`User ${blockPopup.username} (${blockedEmailId}) has been blocked successfully.`);
  
        // ✅ Remove blocked user's comments from the UI
        setComments((prev) => prev.filter((comment) => comment.emailId !== blockedEmailId));
  
        // ✅ Close the block popup
        setBlockPopup({ visible: false, username: "", blockedEmail: "" });
        setBlockedEmailId(""); // ✅ Reset after blocking
      }
    } catch (error) {
      console.error("Error blocking user:", error);
      alert("Failed to block user. Please try again.");
    }
  };
  
  const handleReportUser = async () => {
    if (!reportPopup.reason.trim()) {
      alert("Please provide a reason for reporting.");
      return;
    }
  
    try {
      const response = await axios.post("/api/report-user", {
        reporterEmail: emailId, // User A's email
        reportedEmail: reportPopup.reportedEmail, // User B's email
        reason: reportPopup.reason, // The reason provided
      });
  
      if (response.status === 200) {
        alert(`Report against ${reportPopup.username} has been successfully sent.`);
        setReportPopup({ visible: false, username: "", reportedEmail: "", reporterEmail: "", reason: "" });
      }
    } catch (error) {
      console.error("Error submitting report:", error.message);
      alert("Failed to send report. Please try again.");
    }
  };

  const openReplyBox = (commentId) => {
    setActiveReply(commentId);
    setShowReplyBox(true);
  };

  const handleAddReply = async (commentId) => {
    if (newReply.trim() === "") {
      alert("Reply cannot be empty");
      return;
    }

    const currentDate = new Date();
    const replyData = {
      emailId: emailId,
      username: firstName,
      text: newReply,
      date: currentDate.toISOString().split("T")[0],
      time: currentDate.toTimeString().split(" ")[0],
    };

    try {
      const response = await axios.post(
        `/api/comments/${commentId}/replies`,
        replyData
      );

      if (response.status === 201) {
        const newReplyObj = {
          Comment_Id: Date.now(),
          username: firstName,
          date: currentDate.toISOString().split("T")[0],
          time: currentDate.toTimeString().split(" ")[0],
          text: newReply,
          Likes: 0,
          Dislikes: 0,
        };

        setComments((prev) =>
          prev.map((comment) =>
            comment.Comment_Id === commentId
              ? { ...comment, replies: [newReplyObj, ...comment.replies] }
              : comment
          )
        );
        setNewReply("");
        setActiveReply(null);
        setShowReplyBox(false);
      }
    } catch (error) {
      console.error("Error adding reply:", error);
      alert("Failed to add reply. Please try again.");
    }
  };

  const handleCancelReply = () => {
    setNewReply("");
    setActiveReply(null);
    setShowReplyBox(false);
  };

  const generateAvatar = (name) => {
    if (!name) name = "U";
    const firstLetter = name.charAt(0).toUpperCase();
    const colors = ["#FFD700", "#ADFF2F", "#FF4500", "#1E90FF", "#DA70D6"];
    const color = colors[firstLetter.charCodeAt(0) % colors.length];

    return (
      <div
        style={{
          width: "40px",
          height: "40px",
          borderRadius: "50%",
          backgroundColor: color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          fontSize: "20px",
          fontWeight: "bold",
        }}
      >
        {firstLetter}
      </div>
    );
  };

  const indexOfLastComment = currentPage * COMMENTS_PER_PAGE;
  const indexOfFirstComment = indexOfLastComment - COMMENTS_PER_PAGE;
  const currentComments = comments.slice(
    indexOfFirstComment,
    indexOfLastComment
  );

  const Pagination = () => {
    const totalPages = Math.ceil(comments.length / COMMENTS_PER_PAGE);
    const visiblePages = 5; // Number of pages to display at once
    const startPage = Math.max(1, currentPage - Math.floor(visiblePages / 2));
    const endPage = Math.min(totalPages, startPage + visiblePages - 1);

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "5px",
          marginTop: "20px",
        }}
      >
        <button
          onClick={() => setCurrentPage(1)}
          disabled={currentPage === 1}
          style={getButtonStyle(currentPage === 1)}
        >
          First
        </button>
        <button
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          style={getButtonStyle(currentPage === 1)}
        >
          Back
        </button>
        {Array.from(
          { length: endPage - startPage + 1 },
          (_, index) => startPage + index
        ).map((page) => (
          <button
            key={page}
            onClick={() => setCurrentPage(page)}
            style={{
              padding: "5px 10px",
              backgroundColor: page === currentPage ? "#106cfc" : "#f0f0f0",
              color: page === currentPage ? "white" : "#106cfc",
              border: "1px solid #106cfc",
              borderRadius: "5px",
              cursor: "pointer",
            }}
          >
            {page}
          </button>
        ))}
        <button
          onClick={() =>
            setCurrentPage((prev) => Math.min(prev + 1, totalPages))
          }
          disabled={currentPage === totalPages}
          style={getButtonStyle(currentPage === totalPages)}
        >
          Next
        </button>
        <button
          onClick={() => setCurrentPage(totalPages)}
          disabled={currentPage === totalPages}
          style={getButtonStyle(currentPage === totalPages)}
        >
          Last
        </button>
      </div>
    );
  };

  // Helper function for button styles
  const getButtonStyle = (isDisabled) => ({
    padding: "5px 10px",
    backgroundColor: isDisabled ? "#e0e0e0" : "white",
    color: isDisabled ? "#aaa" : "#106cfc",
    border: "1px solid #106cfc",
    borderRadius: "5px",
    cursor: isDisabled ? "not-allowed" : "pointer",
  });

  return (
    <div className="container">
      <div className="comments-section" style={{ marginTop: "20px" }}>
        {/* Add Comment Section */}
        <div
          className="add-comment"
          style={{ display: "flex", alignItems: "center", gap: "10px" }}
        >
          <div className="user-avatar">{generateAvatar(firstName || "U")}</div>
          <div
            className="comment-input"
            style={{ flex: 1, position: "relative" }}
          >
            <textarea
              className="form-control"
              placeholder="Add a comment..."
              value={newComment}
              onChange={handleNewCommentChange}
              style={{
                width: "100%",
                border: "none",
                borderBottom: "1px solid #ccc",
                outline: "none",
              }}
              rows="1"
            />
            <div
              style={{
                position: "absolute",
                right: "10px",
                bottom: "10px",
                fontSize: "12px",
                color: "#999",
              }}
            >
              {wordCount}/400
            </div>
          </div>
          <button 
            className="btn btn-light" 
            onClick={handleCancel}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              padding: '0'
            }}
          >
            <MdClose size={24} />
          </button>
          <button 
            className="btn btn-primary"
            onClick={handleAddComment}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              padding: '0'
            }}
          >
            <MdSend size={24} />
          </button>
        </div>

        {/* Comments List */}
        <div
          className="comments-list"
          style={{
            marginTop: "20px",
            maxHeight: "300px",
            overflowY: "scroll",
            border: "1px solid #ccc",
            backgroundColor: "#fff",
            padding: "10px",
            borderRadius: "5px",
          }}
        >
          {comments.length === 0 ? (
            <p>Please login to see the comments </p>
          ) : (
            <>
              {currentComments.map((comment) => (
                <div key={comment.Comment_Id} style={{ marginBottom: "20px" }}>
                  {/* Category Name */}
                  {comment.category && (
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "bold",
                        color: "#555",
                        marginBottom: "5px",
                      }}
                    >
                      <div style={{ marginBottom: "3px" }}>
                        # {comment.category}
                      </div>
                    </div>
                  )}

                  {/* Comment */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      marginBottom: "10px",
                    }}
                  >
                    <div style={{ marginRight: "10px" }}>
                      {generateAvatar(comment.Username)}
                    </div>
                    <div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        <strong>@{comment.Username}</strong>
                        <span style={{ fontSize: "12px", color: "#777" }}>
                          {comment.Datestamp} {comment.Timestamp}
                        </span>
                      <MdFlag
                         title="Report"
                         style={{ fontSize: "20px", color: "red", cursor: "pointer" }}
                         onClick={() => {
                           console.log("MdFlag Clicked - User B Email:", comment.emailId); // Debugging log ✅
                           
                           if (!comment.emailId) {
                             alert("Error: Reported user's email ID is missing.");
                             return;
                           }
 
                           setReportPopup({
                             visible: true,
                             username: comment.Username, // User B's username
                             reportedEmail: comment.emailId, // ✅ Set User B's email correctly
                             reporterEmail: emailId, // ✅ User A's email
                             reason: "", // Start with empty reason
                           });
                         }}
                       />
                        <MdBlock
                            title="Block User"
                            style={{ fontSize: "20px", color: "#106cfc", cursor: "pointer" }}
                            onClick={() => {
                              console.log("Blocking User:", comment.Username, "Email:", comment.emailId); // Debugging log ✅
                              
                              if (!comment.emailId) {
                                alert("Error: Blocked user's email ID is missing.");
                                return;
                              }
    
                              // ✅ Store blocked email in state
                              setBlockedEmailId(comment.emailId);
    
                              // ✅ Pass both emails through `blockPopup` (same as Report logic)
                              setBlockPopup({
                                visible: true,
                                username: comment.Username,
                                blockedEmail: comment.emailId,
                                blockerEmail: emailId, // ✅ Store the blocker email here
                              });
                            }}
                          />                  
                       </div>
                      <p style={{ margin: "5px 0" }}>{comment.comments}</p>
                      <p
                        style={{
                          margin: "0",
                          color: "#999",
                          fontWeight: "bold",
                        }}
                      >
                        {/* TODO */}
                        {comment.bill_title && (
                          <div style={{ marginBottom: "3px" }}>
                            # {comment.bill_title}
                          </div>
                        )}
                      </p>{" "}
                      {/* Display # below the comment */}
                    </div>
                  </div>

                  {/* Comment Actions */}
                  <div style={{ display: "flex", gap: "10px", alignItems: "center", marginTop: "10px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                      <button
                        onClick={() => handleLike(comment.Comment_Id)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: "2px solid #106cfc",
                          borderRadius: "50%",
                          width: "40px",
                          height: "40px",
                          backgroundColor: comment.userAction === "like" ? "#106cfc" : "transparent",
                          color: comment.userAction === "like" ? "white" : "#106cfc",
                          cursor: "pointer",
                        }}
                      >
                        <MdThumbUp style={{ fontSize: "20px" }} />
                      </button>
                      <span style={{ 
                        fontSize: "14px", 
                        color: "#666",
                        minWidth: "20px",
                        fontWeight: "500"
                      }}>
                        {comment.Likes || 0}
                      </span>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                      <button
                        onClick={() => handleDislike(comment.Comment_Id)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: "2px solid #106cfc",
                          borderRadius: "50%",
                          width: "40px",
                          height: "40px",
                          backgroundColor: comment.userAction === "dislike" ? "#106cfc" : "transparent",
                          color: comment.userAction === "dislike" ? "white" : "#106cfc",
                          cursor: "pointer",
                        }}
                      >
                        <MdThumbDown style={{ fontSize: "20px" }} />
                      </button>
                      <span style={{ 
                        fontSize: "14px", 
                        color: "#666",
                        minWidth: "20px",
                        fontWeight: "500"
                      }}>
                        {comment.Dislikes || 0}
                      </span>
                    </div>

                    <button
                      onClick={() => openReplyBox(comment.Comment_Id)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                        border: "none",
                        background: "none",
                        cursor: "pointer",
                      }}
                    >
                      <MdChatBubbleOutline
                        style={{ color: "#106cfc", fontSize: "20px" }}
                      />{" "}
                      Reply
                    </button>
                  </div>

                  {/* Replies */}
                  {/* Replies */}
                  <div>
                    {/* <button
                      onClick={() => {
                        if (!comment.repliesLoaded) {
                          fetchReplies(comment.Comment_Id); // Fetch replies only when button is clicked
                        } else {
                          setComments((prev) =>
                            prev.map((c) =>
                              c.Comment_Id === comment.Comment_Id
                                ? { ...c, replies: [], repliesLoaded: false } // Hide replies
                                : c
                            )
                          );
                        }
                      }}
                      style={{
                        marginTop: "10px",
                        border: "1px solid #106cfc",
                        padding: "5px 10px",
                        backgroundColor: comment.repliesLoaded
                          ? "#f0f0f0"
                          : "#106cfc",
                        color: comment.repliesLoaded ? "#106cfc" : "#fff",
                        cursor: "pointer",
                        borderRadius: "5px",
                      }}
                    >
                      {comment.repliesLoaded ? "Hide Replies" : "View Replies"}
                    </button> */}

                    {/* Display replies if loaded */}
                    {comment.repliesLoaded && comment.replies.length > 0 && (
                      <div
                        style={{
                          marginTop: "10px",
                          paddingLeft: "20px",
                          borderLeft: "2px solid #ccc",
                        }}
                      >
                        {comment.replies.map((reply) => (
                          <div
                            key={reply.Recomment_Id}
                            style={{ marginBottom: "10px" }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "flex-start",
                              }}
                            >
                              <div style={{ marginRight: "10px" }}>
                                {generateAvatar(reply.Username)}
                              </div>
                              <div>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                  }}
                                >
                                  <strong>@{reply.Username}</strong>
                                  <span
                                    style={{ fontSize: "12px", color: "#777" }}
                                  >
                                    {new Date(
                                      reply.Datestamp
                                    ).toLocaleDateString()}{" "}
                                    {reply.Timestamp}
                                  </span>
                                </div>
                                <p style={{ margin: "5px 0" }}>
                                  {reply.comments}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              <Pagination />
            </>
          )}
        </div>

        {/* Reply Box */}
        {showReplyBox && activeReply && (
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              backgroundColor: "#fff",
              padding: "20px",
              borderRadius: "10px",
              boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
            }}
          >
            <h4>Reply to Comment</h4>
            <textarea
              value={newReply}
              onChange={(e) => setNewReply(e.target.value)}
              style={{ width: "100%", marginBottom: "10px" }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                className="btn btn-light"
                onClick={handleCancelReply}
                style={{ marginRight: "10px" }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={() => handleAddReply(activeReply)}
              >
                Reply
              </button>
            </div>
          </div>
        )}
      {/* Block Confirmation Popup */}
      {blockPopup.visible && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "#fff",
            padding: "20px",
            borderRadius: "10px",
            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
            textAlign: "center",
          }}
        >
          <h4>Are you sure you want to block {blockPopup.username}?</h4>
          {/* <p><strong>Your Email (Blocker):</strong> {emailId}</p> {/* ✅ Display Blocker's Email */}
          {/* <p><strong>Blocked User Email:</strong> {blockPopup.blockedEmail}</p> ✅ Display Blocked User's Email */}
          <div style={{ marginTop: "10px", display: "flex", justifyContent: "center", gap: "10px" }}>
            <button className="btn btn-light" onClick={() => setBlockPopup({ visible: false, username: "", blockedEmail: "" })}>
              Cancel
            </button>
            <button className="btn btn-danger" onClick={handleBlockUser}>
              Block
            </button>
          </div>
        </div>
      )}

      {/* Report Confirmation Popup */}
      {reportPopup.visible && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "#fff",
            padding: "20px",
            borderRadius: "10px",
            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
            textAlign: "center",
            width: "350px",
          }}
        >
          <h4>Report {reportPopup.username} to the UrVote Admin?</h4>
          {/* <p><strong>Your Email:</strong> {reportPopup.reporterEmail}</p> ✅ Display User A's Email */}
          {/* <p><strong>Reported User Email:</strong> {reportPopup.reportedEmail}</p> ✅ New Line: Display User B's Email */}
          <p>Provide an explanation for reporting:</p>
          <textarea
            placeholder="Enter your reason here..."
            value={reportPopup.reason}
            onChange={(e) =>
              setReportPopup((prev) => ({
                ...prev,
                reason: e.target.value,
              }))
            }
            style={{
              width: "100%",
              height: "80px",
              padding: "5px",
              border: "1px solid #ccc",
              borderRadius: "5px",
              marginBottom: "10px",
            }}
          />
          <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
            <button
              className="btn btn-light"
              onClick={() =>
                setReportPopup({
                  visible: false,
                  username: "",
                  reportedEmail: "",
                  reporterEmail: "",
                  reason: "",
                })
              }
            >
              Cancel
            </button>
            <button
              className="btn btn-danger"
              onClick={handleReportUser}
              disabled={!reportPopup.reason.trim()}
            >
              Report
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

export default Comment;
